import { toast } from 'react-toastify';
import { styled } from '@mui/material/styles';
import DataTable from 'react-data-table-component';
import { CircularProgress } from "@material-ui/core";
import { useDispatch, useSelector } from 'react-redux';
import React, { useState, useEffect, useRef } from 'react';
import { Dropdown, DropdownToggle, DropdownMenu, DropdownItem, Modal, ModalBody, ModalHeader } from 'reactstrap';
import { Box, Button, FormControl, FormControlLabel, IconButton, MenuItem, Select, Switch, TextField, Tooltip, Typography, Chip } from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon, Edit as EditIcon, ExpandMore as ExpandIcon, ExpandLess as CollapseIcon, TextFields, DateRange, ToggleOn, Email, Phone, Link, AttachMoney, Percent, Description, ArrowDownward } from '@mui/icons-material';

import constants from '../../../constants';
import TableAnimationFiles from '../../../components/Animations/TableAnimationFiles.js';
import { getProperties, createProperty, updateProperty, deleteProperty, updateTagGlobally, deleteTagGlobally, createTag } from '../../../store/Objects';

const PropertyManagement = () => {
  const dispatch = useDispatch();
  const [selectedObject, setSelectedObject] = useState('contacts');

  // Select all object states to ensure re-renders on any property updates
  const allObjectsData = useSelector(state => state['Objects']);
  const objectProperties = allObjectsData?.[selectedObject]?.['properties'] || {};

  // Show loading if properties object is empty or undefined
  const isLoading = !objectProperties || Object.keys(objectProperties).length === 0;

  const [properties, setProperties] = useState({});
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedRows, setSelectedRows] = useState([]);
  const [dialogDelete, setDialogDelete] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [selectedGroup, setSelectedGroup] = useState('all');
  const [openDropdownId, setOpenDropdownId] = useState(null);
  const [editingProperty, setEditingProperty] = useState(null);
  const [selectedPropertyId, setSelectedPropertyId] = useState(null);
  const [colWidths, setColWidths] = useState({ name: 250, type: 200, object: 150, group: 200, display: 200 });
  const [formData, setFormData] = useState({ name: '', label: '', description: '', type: 'text', group: 'Custom Properties', objectType: selectedObject, validation: { required: false, unique: false }, displayConfig: { showInList: true, showInForm: true, showInDetail: true }, fieldConfig: {} });

  // Available objects
  const objects = Object.values(constants['objectsSchema']).map(obj => ({ value: obj['type'], label: obj['labels'] }));

  useEffect(() => { dispatch(getProperties()) }, []);
  useEffect(() => { setProperties(allObjectsData?.[selectedObject]?.['properties'] || {}); }, [selectedObject, allObjectsData]);

  const handleObjectChange = (event) => {
    setSelectedObject(event['target']['value']);
    setSelectedGroup('all'); // Reset group filter when objectType changes
  };

  const handleGroupChange = (event) => setSelectedGroup(event['target']['value']);

  const handleSubmit = () => {
    // Validate form
    if (!formData['name'] || !formData['label']) return toast.error('Name and Label are required');

    // Prepare data
    let propertyData = { ...formData, name: formData['name'].toLowerCase().replace(/[^a-z0-9_]/g, '_') };

    // Filter out empty options for dropdown/multiselect fields
    if (['select', 'multiselect'].includes(formData['type']) && formData['fieldConfig']?.['options']) {
      const validOptions = formData['fieldConfig']['options'].filter(opt => opt['value'] && opt['label']);
      if (validOptions.length === 0) {
        return toast.error('Please add at least one option for dropdown field');
      }
      propertyData = {
        ...propertyData,
        fieldConfig: {
          ...propertyData['fieldConfig'],
          options: validOptions
        }
      };
    }

    // If editing the tags property, sync tag changes to object records
    if (editingProperty && editingProperty['name'] === 'tags' && editingProperty['fieldConfig']?.['hasColors']) {
      const oldOptions = editingProperty['fieldConfig']?.['options'] || [];
      const newOptions = propertyData['fieldConfig']?.['options'] || [];
      const objectType = formData['objectType'];

      // Detect removed options
      oldOptions.forEach(oldOpt => {
        const stillExists = newOptions.some(newOpt => newOpt['value'] === oldOpt['value']);
        if (!stillExists) dispatch(deleteTagGlobally({ objectType, tagName: oldOpt['value'] }));
      });

      // Detect renamed or recolored options
      newOptions.forEach(newOpt => {
        const oldOpt = oldOptions.find(o => o['value'] === newOpt['value']);
        if (oldOpt) {
          const nameChanged = oldOpt['label'] !== newOpt['label'] || oldOpt['value'] !== newOpt['value'];
          const colorChanged = oldOpt['color'] !== newOpt['color'];
          if (nameChanged || colorChanged) {
            dispatch(updateTagGlobally({ objectType, oldTagName: oldOpt['value'], newTagName: newOpt['value'], newTagColor: newOpt['color'] }));
          }
        } else {
          // New option that didn't exist before
          dispatch(createTag({ objectType, tagName: newOpt['value'], tagColor: newOpt['color'] }));
        }
      });
    }

    if (editingProperty) dispatch(updateProperty({ objectType: formData['objectType'], propertyId: editingProperty['_id'], updates: propertyData }));
    else dispatch(createProperty({ objectType: formData['objectType'], property: propertyData }));

    resetForm();
    setDialogOpen(false);
  };

  // const handleDelete = (propertyId) => {
  //   if (window.confirm('Are you sure you want to delete this property?'))
  //     dispatch(deleteProperty(propertyId));
  // };

  const handleDelete = () => {
    dispatch(deleteProperty({ objectType: selectedObject, propertyId: selectedPropertyId }));
    setDialogDelete(false);
    setSelectedPropertyId(null);
  };

  const handleEdit = (property) => {
    setEditingProperty(property);
    setFormData({
      name: property['name'],
      label: property['label'],
      description: property['description'] || '',
      type: property['type'],
      group: property['group'],
      objectType: selectedObject,
      validation: property['validation'] || { required: false, unique: false },
      displayConfig: property['displayConfig'] || { showInList: true, showInForm: true, showInDetail: true },
      fieldConfig: property['fieldConfig'] || {}
    });
    setDialogOpen(true);
  };

  const resetForm = () => {
    setEditingProperty(null);
    setFormData({
      name: '',
      label: '',
      description: '',
      type: 'text',
      group: 'Custom Properties',
      objectType: selectedObject,
      validation: { required: false, unique: false },
      displayConfig: { showInList: true, showInForm: true, showInDetail: true },
      fieldConfig: {}
    });
  };

  // sort them by order of appearance in the properties object
  const allProperties = Object.entries(properties)
    .flatMap(([group, props]) => {
      if (selectedGroup !== 'all' && selectedGroup !== group) return [];
      const filtered = searchValue
        ? props.filter(prop =>
          prop.name?.toLowerCase().includes(searchValue) ||
          prop.label?.toLowerCase().includes(searchValue) ||
          prop.type?.toLowerCase().includes(searchValue)
        )
        : props;
      return filtered.map(prop => ({ ...prop, group }));
    })
    .sort((a, b) => (a.order || 0) - (b.order || 0));

  const isPaginated = allProperties.length > 100;
  const headerScrollHeight = isPaginated ? 'calc(100vh - 189px)' : 'calc(100vh - 161px)';
  const responsiveClass = isPaginated ? 'datatable--paginated' : 'datatable--fullheight property-table';

  const propertyTypes = [
    { value: 'text', label: 'Single Line Text', icon: <TextFields /> },
    { value: 'textarea', label: 'Multi-line Text', icon: <Description /> },
    { value: 'number', label: 'Number', icon: <AttachMoney /> },
    { value: 'date', label: 'Date', icon: <DateRange /> },
    { value: 'datetime', label: 'Date & Time', icon: <DateRange /> },
    { value: 'select', label: 'Dropdown', icon: <ArrowDownward /> },
    { value: 'multiselect', label: 'Multi-select', icon: <ArrowDownward /> },
    { value: 'boolean', label: 'Yes/No', icon: <ToggleOn /> },
    { value: 'email', label: 'Email', icon: <Email /> },
    { value: 'phone', label: 'Phone', icon: <Phone /> },
    { value: 'url', label: 'URL', icon: <Link /> },
    { value: 'currency', label: 'Currency', icon: <AttachMoney /> },
    { value: 'percentage', label: 'Percentage', icon: <Percent /> }
  ];

  const setColWidth = (key, value) => setColWidths(prev => ({ ...prev, [key]: value }));
  const columns = [
    {
      sortable: true,
      selector: row => row['name'],
      width: `${colWidths['name']}px`,
      name: <ResizableHeader title="Name" width={colWidths['name']} onResize={w => setColWidth('name', w)} />,
      cell: (row, index) => {
        return (
          <div className="content-area" onClick={(e) => { e.stopPropagation(); handleEdit(row); }}>
            <Typography variant="body2"> {row['label']} {row['validation']?.['required'] ? '*' : ''} </Typography>
            <Dropdown className="create-btn dropdwon-email" isOpen={openDropdownId === index} toggle={() => setOpenDropdownId(openDropdownId === index ? null : index)}>
              <DropdownToggle onClick={(e) => { e.stopPropagation(); setOpenDropdownId(openDropdownId === index ? null : index); }}>
                <svg width="20px" height="20px" viewBox="0 0 16 16" xmlns="http://www.w3.org/2000/svg" fill="#4b5563" class="bi bi-three-dots-vertical">
                  <path d="M9.5 13a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0zm0-5a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0zm0-5a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0z" />
                </svg>
              </DropdownToggle>
              <DropdownMenu>
                <DropdownItem> <button onClick={(e) => { e.stopPropagation(); handleEdit(row); }}><EditIcon fontSize="small" /> Edit</button> </DropdownItem>
                <DropdownItem disabled={row['isSystem']}> <button onClick={(e) => { e.stopPropagation(); setSelectedPropertyId(row['_id']); setDialogDelete(true); }} disabled={row['isSystem']}><DeleteIcon fontSize="small" /> Delete</button></DropdownItem>
              </DropdownMenu>
            </Dropdown>
          </div>
        )
      }
    },
    {
      sortable: true,
      width: `${colWidths.type}px`,
      selector: row => row['type'],
      name: <ResizableHeader title="Type" width={colWidths.type} onResize={w => setColWidth('type', w)} />,
      cell: row => {
        const typeValue = row['type'] || '';
        const typeClass = `group-${typeValue.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9\-]/g, '')}`;
        const systemClass = `group-${typeValue.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9\-]/g, '')}`;
        return <Box display="flex" gap={0.5} alignItems="center"><Chip className={typeClass} label={row['type']} size="small" variant="outlined" />{row['isSystem'] && <Chip className={systemClass} label="System" size="small" color="secondary" />}</Box>
      },
    },
    {
      sortable: true,
      width: `${colWidths.object}px`,
      selector: () => selectedObject,
      name: <ResizableHeader title="Object" width={colWidths.object} onResize={w => setColWidth('object', w)} />,
      cell: () => {
        const schema = constants.getObjectSchema(selectedObject);
        return <Chip label={schema?.labels || selectedObject} size="small" variant="outlined" />
      },
    },
    {
      sortable: true,
      width: `${colWidths.group}px`,
      selector: row => row['group'],
      cell: row => {
        const groupClass = `group-${row['group']?.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9\-]/g, '')}`;
        return <Chip className={groupClass} label={row['group']} size="small" variant="outlined" />
      }
      ,
      name: <ResizableHeader title="Group" width={colWidths.group} onResize={w => setColWidth('group', w)} />,
    },
    {
      sortable: true,
      width: `${colWidths.display}px`,
      selector: row => row['display'],
      name: <ResizableHeader title="Display" width={colWidths.display} onResize={w => setColWidth('display', w)} />,
      cell: row =>
        <Box display="flex" gap={0.5}>
          {row['displayConfig']?.['showInList'] && <Tooltip title="Shows in overview"><Chip label="O" size="small" /></Tooltip>}
          {row['displayConfig']?.['showInAbout'] && <Tooltip title="Shows in about"><Chip label="A" size="small" /></Tooltip>}
          {row['displayConfig']?.['showInForm'] && <Tooltip title="Shows in form"><Chip label="F" size="small" /></Tooltip>}
          {row['displayConfig']?.['showInDetail'] && <Tooltip title="Shows in detail"><Chip label="D" size="small" /></Tooltip>}
        </Box>,
    }
  ];

  const CustomSortIcon = () => (
    <span className="custom-sort-icon">
      <span className="up">▲</span>
      <span className="down">▼</span>
    </span>
  );

  const renderPropertyForm = () => (
    <>
      {/* --------------- Add/edit propety Modal--------------- */}
      <Modal isOpen={dialogOpen} onClose={() => setDialogOpen(false)} modalClassName='right right-without-bg' className={`main-modal right right-side-modal style-two property-modal`}>
        <ModalHeader toggle={() => setDialogOpen(false)}><h3>{editingProperty ? 'Edit Property' : 'Create New Property'}</h3></ModalHeader>
        <ModalBody>
          <div className='filters-modal-content'>
            <div className='createdeal-form'>
              <div className='row'>
                <div className='col-lg-6 col-md-12'>
                  <div className='group-form'>
                    <label>Display Label</label>
                    <TextField
                      required
                      fullWidth
                      withoutlabel
                      value={formData['label']}
                      placeholder="Add Display Label"
                      onChange={(e) => {
                        const newLabel = e['target']['value'];

                        // Check if the current name was auto-generated from the previous label
                        const currentName = formData['name'] || '';
                        const expectedAutoName = formData['label']?.toLowerCase().replace(/[^a-z0-9_]/g, '_') || '';
                        const isAutoGenerated = currentName === '' || currentName === expectedAutoName;

                        // Update form data
                        const updates = { label: newLabel }

                        // Auto-generate name if it was previously auto-generated and not editing system property
                        if (isAutoGenerated && !editingProperty?.['isSystem']) updates['name'] = newLabel.toLowerCase().replace(/[^a-z0-9_]/g, '_');

                        setFormData({ ...formData, ...updates });
                      }}
                    />
                  </div>
                </div>
                <div className='col-lg-6 col-md-12'>
                  <div className='group-form'>
                    <label>Internal Name *</label>
                    <TextField
                      fullWidth
                      withoutlabel
                      value={formData['name']}
                      placeholder="Add internal name (auto-generated)"
                      disabled={editingProperty?.['isSystem']}
                      // helperText="lowercase letters, numbers, and underscores only"
                      onChange={(e) => setFormData({ ...formData, name: e['target']['value'] })}
                    />
                  </div>
                </div>
                <div className='col-lg-12 col-md-12'>
                  <label>Description</label>
                  <div className='group-form'>
                    <TextField
                      rows={1}
                      fullWidth
                      multiline
                      withoutlabel
                      placeholder="Add description"
                      value={formData['description']}
                      onChange={(e) => setFormData({ ...formData, description: e['target']['value'] })}
                    />
                  </div>
                </div>
                <div className='col-lg-12 col-md-12'>
                  <div className='group-form'>
                    <label>Object Type</label>
                    <FormControl fullWidth>
                      <Select
                        value={formData['objectType'] || selectedObject}
                        onChange={(e) => setFormData({ ...formData, objectType: e['target']['value'] })}
                        disabled={editingProperty?.['isSystem']}
                      >
                        {objects.map(objectType => (
                          <MenuItem key={objectType['value']} value={objectType['value']}>
                            {objectType['label']}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </div>
                </div>
                <div className='col-lg-6 col-md-12'>
                  <div className='group-form'>
                    <label>Property Type</label>
                    <FormControl fullWidth>
                      <Select value={formData['type']} onChange={(e) => setFormData({ ...formData, type: e['target']['value'] })} disabled={editingProperty?.['isSystem']}>
                        {propertyTypes.map(type => <MenuItem key={type['value']} value={type['value']}> <Box display="flex" alignItems="center" gap={1}> {type['icon']} {type['label']} </Box> </MenuItem>)}
                      </Select>
                    </FormControl>
                  </div>
                </div>
                <div className='col-lg-6 col-md-12'>
                  <div className='group-form'>
                    <label>Group</label>
                    <FormControl fullWidth>
                      <Select value={formData['group']} onChange={(e) => setFormData({ ...formData, group: e['target']['value'] })}>
                        {Object.keys(allObjectsData?.[formData['objectType'] || selectedObject]?.['properties'] || properties || {}).filter(group => group !== 'Basic Information').map(group => <MenuItem key={group} value={group}>{group}</MenuItem>)}
                      </Select>
                    </FormControl>
                  </div>
                </div>
                {['select', 'multiselect'].includes(formData['type']) && (
                  <div className='col-lg-12 col-md-12'>
                    <div className='group-form'>
                      <label>Options</label>
                      {/* Type-specific configuration */}
                      <OptionsEditor options={formData['fieldConfig']['options'] || []} hasColors={formData['fieldConfig']['hasColors']} onChange={(options) => setFormData({ ...formData, fieldConfig: { ...formData['fieldConfig'], options } })} />
                    </div>
                  </div>
                )}
                <div className='col-lg-12'>
                  <div className='group-form checkbox-group'>
                    <h4>Validation</h4>
                    <FormControlLabel
                      className='switch-label'
                      control={
                        <CustomSwitch
                          checked={formData['validation']['required']}
                          onChange={(e) => setFormData({ ...formData, validation: { ...formData['validation'], required: e['target']['checked'] } })}
                        />
                      }
                      label="Required field"
                    />
                    <FormControlLabel
                      className='switch-label'
                      control={
                        <CustomSwitch
                          checked={formData['validation']['unique']}
                          onChange={(e) => setFormData({ ...formData, validation: { ...formData['validation'], unique: e['target']['checked'] } })}
                        />
                      }
                      label="Unique values only"
                    />
                  </div>
                </div>
                <div className='col-lg-12'>
                  <div className='group-form checkbox-group'>
                    <h4>Display Settings</h4>
                    <FormControlLabel
                      className='switch-label'
                      control={
                        <CustomSwitch
                          checked={formData['displayConfig']['showInList']}
                          onChange={(e) => setFormData({ ...formData, displayConfig: { ...formData['displayConfig'], showInList: e['target']['checked'] } })}
                        />
                      }
                      label="Show in overview"
                    />
                    <FormControlLabel
                      className='switch-label'
                      control={
                        <CustomSwitch
                          checked={formData['displayConfig']['showInAbout']}
                          onChange={(e) => setFormData({ ...formData, displayConfig: { ...formData['displayConfig'], showInAbout: e['target']['checked'] } })}
                        />
                      }
                      label="Show in about view"
                    />
                    <FormControlLabel
                      className='switch-label'
                      control={
                        <CustomSwitch
                          checked={formData['displayConfig']['showInDetail']}
                          onChange={(e) => setFormData({ ...formData, displayConfig: { ...formData['displayConfig'], showInDetail: e['target']['checked'] } })}
                        />
                      }
                      label="Show in detail view"
                    />
                    <FormControlLabel
                      className='switch-label'
                      control={
                        <CustomSwitch
                          checked={formData['displayConfig']['showInForm']}
                          onChange={(e) => setFormData({ ...formData, displayConfig: { ...formData['displayConfig'], showInForm: e['target']['checked'] } })}
                        />
                      }
                      label="Show in add/edit forms"
                    />
                  </div>
                </div>
              </div>
            </div>
            <div className='group-form btn-groups'>
              <button className='btn-style-border' onClick={() => { setDialogOpen(false); resetForm(); }}>Cancel</button>
              <button className='btn-style-new' onClick={handleSubmit}>
                {editingProperty ? 'Update' : 'Create'} Property
              </button>
            </div>
          </div>
        </ModalBody>
      </Modal>

      {/* --------------- Add/edit propety Modal--------------- */}
      <Modal isOpen={dialogDelete} onClose={() => setDialogDelete(false)} className={`main-modal new-user-modal create-property-modal modal-dialog-centered`}>
        <ModalHeader toggle={() => setDialogDelete(false)}>
          <h3>Delete Property</h3>
        </ModalHeader>
        <ModalBody>
          <div className='credits-area workspace-modal'>
            <p>Are you sure you want to delete this property?</p>
            <div className='footer-modal-area'>
              <button className='btn-style-border' onClick={() => setDialogDelete(false)}>Cancel</button>
              <button className='btn-style-new' onClick={() => handleDelete(selectedPropertyId)}>Delete</button>
            </div>
          </div>
        </ModalBody>
      </Modal>
    </>
  );

  return (
    <Box>
      <div className='header-table-crm'>
        <h3>Property</h3>
        <button variant="contained" className='btn-style-new' onClick={() => { resetForm(); setDialogOpen(true); }}><AddIcon /> Add Property</button>
      </div>
      {/* Display all properties in a single table */}
      <div className="table-container filters-table mt-3 property-tables-area">
        {selectedRows.length === 1 ? (
          <div className='header-table-filters advanced-table-filters style-filter-show'>
            <div className='left-area'>
              <p>1 contact selected</p>
              <button className='assing-btn'>
                <i className='icon'>
                  <svg width="12" height="6" viewBox="0 0 12 6" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M9.00049 5L11.0672 2.93327L9.00049 0.866576" stroke="var(--primary-color)" stroke-linecap="round" stroke-linejoin="round" />
                    <path d="M1.06689 2.93359L11.0671 2.93359" stroke="var(--primary-color)" stroke-linecap="round" stroke-linejoin="round" />
                  </svg>
                </i>
                Assign
              </button>
              <button className='assing-btn'>
                <i className='icon'>
                  <svg width="12" height="11" viewBox="0 0 12 11" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M6.14459 1.64981L2.38167 5.63273C2.23959 5.78398 2.10209 6.0819 2.07459 6.28815L1.90501 7.77315C1.84542 8.3094 2.23042 8.67606 2.76209 8.5844L4.23792 8.33232C4.44417 8.29565 4.73292 8.1444 4.87501 7.98856L8.63792 4.00565C9.28876 3.31815 9.58209 2.5344 8.56917 1.57648C7.56084 0.627731 6.79542 0.962314 6.14459 1.64981Z" stroke="var(--primary-color)" stroke-width="0.6875" stroke-miterlimit="10" stroke-linecap="round" stroke-linejoin="round" />
                    <path d="M5.5166 2.31445C5.71368 3.57945 6.74035 4.54654 8.01452 4.67487" stroke="var(--primary-color)" stroke-width="0.6875" stroke-miterlimit="10" stroke-linecap="round" stroke-linejoin="round" />
                    <path d="M1.44189 10.0835H9.69189" stroke="var(--primary-color)" stroke-width="0.6875" stroke-miterlimit="10" stroke-linecap="round" stroke-linejoin="round" />
                  </svg>
                </i>
                Edit
              </button>
              <button className='assing-btn'>
                <i className='icon'>
                  <svg width="12" height="11" viewBox="0 0 12 11" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M9.69189 2.74089C8.16564 2.58964 6.63023 2.51172 5.09939 2.51172C4.19189 2.51172 3.28439 2.55755 2.37689 2.64922L1.44189 2.74089" stroke="#4B5563" stroke-width="0.6875" stroke-linecap="round" stroke-linejoin="round" />
                    <path d="M3.96289 2.27775L4.06372 1.67734C4.13706 1.24192 4.19206 0.916504 4.96664 0.916504H6.16747C6.94206 0.916504 7.00164 1.26025 7.07039 1.68192L7.17122 2.27775" stroke="#4B5563" stroke-width="0.6875" stroke-linecap="round" stroke-linejoin="round" />
                    <path d="M8.70641 4.18945L8.4085 8.80487C8.35808 9.52445 8.31683 10.0836 7.03808 10.0836H4.09558C2.81683 10.0836 2.77558 9.52445 2.72516 8.80487L2.42725 4.18945" stroke="#4B5563" stroke-width="0.6875" stroke-linecap="round" stroke-linejoin="round" />
                    <path d="M4.80176 7.5625H6.32801" stroke="#4B5563" stroke-width="0.6875" stroke-linecap="round" stroke-linejoin="round" />
                    <path d="M4.4209 5.729H6.71257" stroke="#4B5563" stroke-width="0.6875" stroke-linecap="round" stroke-linejoin="round" />
                  </svg>
                </i>
                Delete
              </button>

              <button className='assing-btn'>
                <i className='icon'>
                  <svg width="11" height="11" viewBox="0 0 11 11" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M1.06689 5.5H10.0669" stroke="var(--primary-color)" stroke-width="1.125" stroke-linecap="round" stroke-linejoin="round" />
                    <path d="M5.56689 10V1" stroke="var(--primary-color)" stroke-width="1.125" stroke-linecap="round" stroke-linejoin="round" />
                  </svg>
                </i>
                Create tasks
              </button>

              <button className='assing-btn'>
                <i className='icon'>
                  <svg width="12" height="11" viewBox="0 0 12 11" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <g clip-path="url(#clip0_1030_1905)">
                      <path d="M4.65007 2.75H7.40007M4.65007 6.41667H7.40007M4.65007 4.58333H10.1501M4.65007 8.25H10.1501M1.44173 4.58333H2.3584C2.61153 4.58333 2.81673 4.37813 2.81673 4.125V3.20833C2.81673 2.9552 2.61153 2.75 2.3584 2.75H1.44173C1.1886 2.75 0.983398 2.9552 0.983398 3.20833V4.125C0.983398 4.37813 1.1886 4.58333 1.44173 4.58333ZM1.44173 8.25H2.3584C2.61153 8.25 2.81673 8.0448 2.81673 7.79167V6.875C2.81673 6.62187 2.61153 6.41667 2.3584 6.41667H1.44173C1.1886 6.41667 0.983398 6.62187 0.983398 6.875V7.79167C0.983398 8.0448 1.1886 8.25 1.44173 8.25Z" stroke="var(--primary-color)" stroke-width="0.6875" stroke-linecap="round" />
                    </g>
                    <defs>
                      <clipPath id="clip0_1030_1905">
                        <rect width="11" height="11" fill="white" transform="translate(0.0668945)" />
                      </clipPath>
                    </defs>
                  </svg>
                </i>
                Add to static list
              </button>
            </div>
            <div className='right-side-filter'>
              <button className='close' onClick={() => setSelectedRows([])}>
                <svg width="10" height="10" viewBox="0 0 10 10" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <mask id="path-1-inside-1_1030_1913" fill="white">
                    <path d="M8.21204 0.303865C8.6172 -0.100965 9.28744 -0.101115 9.69251 0.303865C10.0977 0.709019 10.0977 1.38016 9.69251 1.78531L6.47962 4.9982L9.69251 8.21109C10.0977 8.61625 10.0977 9.28738 9.69251 9.69254C9.48301 9.902 9.21762 10.0001 8.95227 10.0002C8.68695 10.0002 8.42155 9.9019 8.21204 9.69254L4.99817 6.47867L1.78528 9.69254C1.58981 9.90197 1.31035 10 1.04504 10.0002C0.779599 10.0002 0.513396 9.9021 0.303834 9.69254C-0.101246 9.28738 -0.101295 8.61622 0.303834 8.21109L3.51672 4.9982L0.303834 1.78531C-0.101299 1.38018 -0.101257 0.709025 0.303834 0.303865C0.708987 -0.101288 1.38013 -0.101288 1.78528 0.303865L4.99817 3.51676L8.21204 0.303865Z" />
                  </mask>
                  <path d="M8.21204 0.303865C8.6172 -0.100965 9.28744 -0.101115 9.69251 0.303865C10.0977 0.709019 10.0977 1.38016 9.69251 1.78531L6.47962 4.9982L9.69251 8.21109C10.0977 8.61625 10.0977 9.28738 9.69251 9.69254C9.48301 9.902 9.21762 10.0001 8.95227 10.0002C8.68695 10.0002 8.42155 9.9019 8.21204 9.69254L4.99817 6.47867L1.78528 9.69254C1.58981 9.90197 1.31035 10 1.04504 10.0002C0.779599 10.0002 0.513396 9.9021 0.303834 9.69254C-0.101246 9.28738 -0.101295 8.61622 0.303834 8.21109L3.51672 4.9982L0.303834 1.78531C-0.101299 1.38018 -0.101257 0.709025 0.303834 0.303865C0.708987 -0.101288 1.38013 -0.101288 1.78528 0.303865L4.99817 3.51676L8.21204 0.303865Z" fill="var(--primary-color)" />
                  <path d="M8.21204 0.303865L7.94019 0.0318008L7.94012 0.0318703L8.21204 0.303865ZM9.69251 0.303865L9.96446 0.0319116L9.96443 0.0318845L9.69251 0.303865ZM6.47962 4.9982L6.20766 4.72625L5.93571 4.9982L6.20766 5.27015L6.47962 4.9982ZM9.69251 9.69254L9.96444 9.96451L9.96446 9.96449L9.69251 9.69254ZM8.95227 10.0002V10.3848H8.95235L8.95227 10.0002ZM8.21204 9.69254L7.94008 9.96449L7.94018 9.96459L8.21204 9.69254ZM4.99817 6.47867L5.27012 6.20672L4.99813 5.93472L4.72617 6.20676L4.99817 6.47867ZM1.78528 9.69254L1.51312 9.42046L1.50411 9.43012L1.78528 9.69254ZM1.04504 10.0002V10.3848H1.04522L1.04504 10.0002ZM0.303834 9.69254L0.0318532 9.96446L0.0318803 9.96449L0.303834 9.69254ZM0.303834 8.21109L0.575787 8.48305L0.303834 8.21109ZM3.51672 4.9982L3.78868 5.27015L4.06063 4.9982L3.78868 4.72625L3.51672 4.9982ZM0.303834 0.303865L0.0318803 0.0319116L0.0318571 0.0319349L0.303834 0.303865ZM4.99817 3.51676L4.72622 3.78871L4.99813 4.06062L5.27008 3.78875L4.99817 3.51676ZM8.21204 0.303865L8.48388 0.575929C8.73895 0.321065 9.16579 0.321107 9.42058 0.575846L9.69251 0.303865L9.96443 0.0318845C9.4091 -0.523338 8.49544 -0.522995 7.94019 0.0318008L8.21204 0.303865ZM9.69251 0.303865L9.42055 0.575819C9.67551 0.830776 9.67551 1.2584 9.42055 1.51336L9.69251 1.78531L9.96446 2.05726C10.5198 1.50191 10.5198 0.587261 9.96446 0.0319116L9.69251 0.303865ZM9.69251 1.78531L9.42055 1.51336L6.20766 4.72625L6.47962 4.9982L6.75157 5.27015L9.96446 2.05726L9.69251 1.78531ZM6.47962 4.9982L6.20766 5.27015L9.42055 8.48305L9.69251 8.21109L9.96446 7.93914L6.75157 4.72625L6.47962 4.9982ZM9.69251 8.21109L9.42055 8.48305C9.67551 8.738 9.67551 9.16563 9.42055 9.42058L9.69251 9.69254L9.96446 9.96449C10.5198 9.40914 10.5198 8.49449 9.96446 7.93914L9.69251 8.21109ZM9.69251 9.69254L9.42057 9.42056C9.28679 9.55432 9.12078 9.61552 8.95219 9.61555L8.95227 10.0002L8.95235 10.3848C9.31445 10.3847 9.67923 10.2497 9.96444 9.96451L9.69251 9.69254ZM8.95227 10.0002V9.61555C8.7839 9.61555 8.61783 9.55432 8.48389 9.42048L8.21204 9.69254L7.94018 9.96459C8.22528 10.2495 8.58999 10.3848 8.95227 10.3848V10.0002ZM8.21204 9.69254L8.48399 9.42058L5.27012 6.20672L4.99817 6.47867L4.72622 6.75062L7.94008 9.96449L8.21204 9.69254ZM4.99817 6.47867L4.72617 6.20676L1.51328 9.42062L1.78528 9.69254L2.05727 9.96445L5.27016 6.75058L4.99817 6.47867ZM1.78528 9.69254L1.50411 9.43012C1.39292 9.54926 1.22188 9.61548 1.04487 9.61555L1.04504 10.0002L1.04522 10.3848C1.39881 10.3846 1.78669 10.2547 2.06644 9.95496L1.78528 9.69254ZM1.04504 10.0002V9.61555C0.876053 9.61555 0.709392 9.55419 0.575787 9.42058L0.303834 9.69254L0.0318803 9.96449C0.3174 10.25 0.683146 10.3848 1.04504 10.3848V10.0002ZM0.303834 9.69254L0.575814 9.42061C0.320867 9.16561 0.320881 8.73795 0.575787 8.48305L0.303834 8.21109L0.0318803 7.93914C-0.523471 8.49449 -0.52336 9.40914 0.0318532 9.96446L0.303834 9.69254ZM0.303834 8.21109L0.575787 8.48305L3.78868 5.27015L3.51672 4.9982L3.24477 4.72625L0.0318803 7.93914L0.303834 8.21109ZM3.51672 4.9982L3.78868 4.72625L0.575787 1.51336L0.303834 1.78531L0.0318803 2.05726L3.24477 5.27015L3.51672 4.9982ZM0.303834 1.78531L0.575787 1.51336C0.320873 1.25844 0.320862 0.830788 0.57581 0.575795L0.303834 0.303865L0.0318571 0.0319349C-0.523376 0.587263 -0.523471 1.50191 0.0318803 2.05726L0.303834 1.78531ZM0.303834 0.303865L0.575787 0.575819C0.830745 0.320861 1.25837 0.320861 1.51333 0.575819L1.78528 0.303865L2.05723 0.0319116C1.50188 -0.523438 0.58723 -0.523438 0.0318803 0.0319116L0.303834 0.303865ZM1.78528 0.303865L1.51333 0.575819L4.72622 3.78871L4.99817 3.51676L5.27012 3.2448L2.05723 0.0319116L1.78528 0.303865ZM4.99817 3.51676L5.27008 3.78875L8.48395 0.57586L8.21204 0.303865L7.94012 0.0318703L4.72626 3.24476L4.99817 3.51676Z" fill="white" mask="url(#path-1-inside-1_1030_1913)" />
                </svg>
              </button>
            </div>
          </div>
        ) : selectedRows.length > 1 ? (
          <div className='header-table-filters advanced-table-filters style-filter-show'>
            <div className='left-area'>
              <p>20 contact selected</p>
              <button className='assing-btn'>
                <i className='icon'>
                  <svg width="12" height="6" viewBox="0 0 12 6" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M9.00049 5L11.0672 2.93327L9.00049 0.866576" stroke="var(--primary-color)" stroke-linecap="round" stroke-linejoin="round" />
                    <path d="M1.06689 2.93359L11.0671 2.93359" stroke="var(--primary-color)" stroke-linecap="round" stroke-linejoin="round" />
                  </svg>
                </i>
                Assign
              </button>
              <button className='assing-btn'>
                <i className='icon'>
                  <svg width="12" height="11" viewBox="0 0 12 11" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M6.14459 1.64981L2.38167 5.63273C2.23959 5.78398 2.10209 6.0819 2.07459 6.28815L1.90501 7.77315C1.84542 8.3094 2.23042 8.67606 2.76209 8.5844L4.23792 8.33232C4.44417 8.29565 4.73292 8.1444 4.87501 7.98856L8.63792 4.00565C9.28876 3.31815 9.58209 2.5344 8.56917 1.57648C7.56084 0.627731 6.79542 0.962314 6.14459 1.64981Z" stroke="var(--primary-color)" stroke-width="0.6875" stroke-miterlimit="10" stroke-linecap="round" stroke-linejoin="round" />
                    <path d="M5.5166 2.31445C5.71368 3.57945 6.74035 4.54654 8.01452 4.67487" stroke="var(--primary-color)" stroke-width="0.6875" stroke-miterlimit="10" stroke-linecap="round" stroke-linejoin="round" />
                    <path d="M1.44189 10.0835H9.69189" stroke="var(--primary-color)" stroke-width="0.6875" stroke-miterlimit="10" stroke-linecap="round" stroke-linejoin="round" />
                  </svg>
                </i>
                Edit
              </button>
              <button className='assing-btn'>
                <i className='icon'>
                  <svg width="12" height="11" viewBox="0 0 12 11" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M9.69189 2.74089C8.16564 2.58964 6.63023 2.51172 5.09939 2.51172C4.19189 2.51172 3.28439 2.55755 2.37689 2.64922L1.44189 2.74089" stroke="#4B5563" stroke-width="0.6875" stroke-linecap="round" stroke-linejoin="round" />
                    <path d="M3.96289 2.27775L4.06372 1.67734C4.13706 1.24192 4.19206 0.916504 4.96664 0.916504H6.16747C6.94206 0.916504 7.00164 1.26025 7.07039 1.68192L7.17122 2.27775" stroke="#4B5563" stroke-width="0.6875" stroke-linecap="round" stroke-linejoin="round" />
                    <path d="M8.70641 4.18945L8.4085 8.80487C8.35808 9.52445 8.31683 10.0836 7.03808 10.0836H4.09558C2.81683 10.0836 2.77558 9.52445 2.72516 8.80487L2.42725 4.18945" stroke="#4B5563" stroke-width="0.6875" stroke-linecap="round" stroke-linejoin="round" />
                    <path d="M4.80176 7.5625H6.32801" stroke="#4B5563" stroke-width="0.6875" stroke-linecap="round" stroke-linejoin="round" />
                    <path d="M4.4209 5.729H6.71257" stroke="#4B5563" stroke-width="0.6875" stroke-linecap="round" stroke-linejoin="round" />
                  </svg>
                </i>
                Delete
              </button>

              <button className='assing-btn'>
                <i className='icon'>
                  <svg width="11" height="11" viewBox="0 0 11 11" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M1.06689 5.5H10.0669" stroke="var(--primary-color)" stroke-width="1.125" stroke-linecap="round" stroke-linejoin="round" />
                    <path d="M5.56689 10V1" stroke="var(--primary-color)" stroke-width="1.125" stroke-linecap="round" stroke-linejoin="round" />
                  </svg>
                </i>
                Create tasks
              </button>

              <button className='assing-btn'>
                <i className='icon'>
                  <svg width="12" height="11" viewBox="0 0 12 11" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <g clip-path="url(#clip0_1030_1905)">
                      <path d="M4.65007 2.75H7.40007M4.65007 6.41667H7.40007M4.65007 4.58333H10.1501M4.65007 8.25H10.1501M1.44173 4.58333H2.3584C2.61153 4.58333 2.81673 4.37813 2.81673 4.125V3.20833C2.81673 2.9552 2.61153 2.75 2.3584 2.75H1.44173C1.1886 2.75 0.983398 2.9552 0.983398 3.20833V4.125C0.983398 4.37813 1.1886 4.58333 1.44173 4.58333ZM1.44173 8.25H2.3584C2.61153 8.25 2.81673 8.0448 2.81673 7.79167V6.875C2.81673 6.62187 2.61153 6.41667 2.3584 6.41667H1.44173C1.1886 6.41667 0.983398 6.62187 0.983398 6.875V7.79167C0.983398 8.0448 1.1886 8.25 1.44173 8.25Z" stroke="var(--primary-color)" stroke-width="0.6875" stroke-linecap="round" />
                    </g>
                    <defs>
                      <clipPath id="clip0_1030_1905">
                        <rect width="11" height="11" fill="white" transform="translate(0.0668945)" />
                      </clipPath>
                    </defs>
                  </svg>
                </i>
                Add to static list
              </button>
            </div>
            <div className='right-side-filter'>
              <button className='close' onClick={() => setSelectedRows([])}>
                <svg width="10" height="10" viewBox="0 0 10 10" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <mask id="path-1-inside-1_1030_1913" fill="white">
                    <path d="M8.21204 0.303865C8.6172 -0.100965 9.28744 -0.101115 9.69251 0.303865C10.0977 0.709019 10.0977 1.38016 9.69251 1.78531L6.47962 4.9982L9.69251 8.21109C10.0977 8.61625 10.0977 9.28738 9.69251 9.69254C9.48301 9.902 9.21762 10.0001 8.95227 10.0002C8.68695 10.0002 8.42155 9.9019 8.21204 9.69254L4.99817 6.47867L1.78528 9.69254C1.58981 9.90197 1.31035 10 1.04504 10.0002C0.779599 10.0002 0.513396 9.9021 0.303834 9.69254C-0.101246 9.28738 -0.101295 8.61622 0.303834 8.21109L3.51672 4.9982L0.303834 1.78531C-0.101299 1.38018 -0.101257 0.709025 0.303834 0.303865C0.708987 -0.101288 1.38013 -0.101288 1.78528 0.303865L4.99817 3.51676L8.21204 0.303865Z" />
                  </mask>
                  <path d="M8.21204 0.303865C8.6172 -0.100965 9.28744 -0.101115 9.69251 0.303865C10.0977 0.709019 10.0977 1.38016 9.69251 1.78531L6.47962 4.9982L9.69251 8.21109C10.0977 8.61625 10.0977 9.28738 9.69251 9.69254C9.48301 9.902 9.21762 10.0001 8.95227 10.0002C8.68695 10.0002 8.42155 9.9019 8.21204 9.69254L4.99817 6.47867L1.78528 9.69254C1.58981 9.90197 1.31035 10 1.04504 10.0002C0.779599 10.0002 0.513396 9.9021 0.303834 9.69254C-0.101246 9.28738 -0.101295 8.61622 0.303834 8.21109L3.51672 4.9982L0.303834 1.78531C-0.101299 1.38018 -0.101257 0.709025 0.303834 0.303865C0.708987 -0.101288 1.38013 -0.101288 1.78528 0.303865L4.99817 3.51676L8.21204 0.303865Z" fill="var(--primary-color)" />
                  <path d="M8.21204 0.303865L7.94019 0.0318008L7.94012 0.0318703L8.21204 0.303865ZM9.69251 0.303865L9.96446 0.0319116L9.96443 0.0318845L9.69251 0.303865ZM6.47962 4.9982L6.20766 4.72625L5.93571 4.9982L6.20766 5.27015L6.47962 4.9982ZM9.69251 9.69254L9.96444 9.96451L9.96446 9.96449L9.69251 9.69254ZM8.95227 10.0002V10.3848H8.95235L8.95227 10.0002ZM8.21204 9.69254L7.94008 9.96449L7.94018 9.96459L8.21204 9.69254ZM4.99817 6.47867L5.27012 6.20672L4.99813 5.93472L4.72617 6.20676L4.99817 6.47867ZM1.78528 9.69254L1.51312 9.42046L1.50411 9.43012L1.78528 9.69254ZM1.04504 10.0002V10.3848H1.04522L1.04504 10.0002ZM0.303834 9.69254L0.0318532 9.96446L0.0318803 9.96449L0.303834 9.69254ZM0.303834 8.21109L0.575787 8.48305L0.303834 8.21109ZM3.51672 4.9982L3.78868 5.27015L4.06063 4.9982L3.78868 4.72625L3.51672 4.9982ZM0.303834 0.303865L0.0318803 0.0319116L0.0318571 0.0319349L0.303834 0.303865ZM4.99817 3.51676L4.72622 3.78871L4.99813 4.06062L5.27008 3.78875L4.99817 3.51676ZM8.21204 0.303865L8.48388 0.575929C8.73895 0.321065 9.16579 0.321107 9.42058 0.575846L9.69251 0.303865L9.96443 0.0318845C9.4091 -0.523338 8.49544 -0.522995 7.94019 0.0318008L8.21204 0.303865ZM9.69251 0.303865L9.42055 0.575819C9.67551 0.830776 9.67551 1.2584 9.42055 1.51336L9.69251 1.78531L9.96446 2.05726C10.5198 1.50191 10.5198 0.587261 9.96446 0.0319116L9.69251 0.303865ZM9.69251 1.78531L9.42055 1.51336L6.20766 4.72625L6.47962 4.9982L6.75157 5.27015L9.96446 2.05726L9.69251 1.78531ZM6.47962 4.9982L6.20766 5.27015L9.42055 8.48305L9.69251 8.21109L9.96446 7.93914L6.75157 4.72625L6.47962 4.9982ZM9.69251 8.21109L9.42055 8.48305C9.67551 8.738 9.67551 9.16563 9.42055 9.42058L9.69251 9.69254L9.96446 9.96449C10.5198 9.40914 10.5198 8.49449 9.96446 7.93914L9.69251 8.21109ZM9.69251 9.69254L9.42057 9.42056C9.28679 9.55432 9.12078 9.61552 8.95219 9.61555L8.95227 10.0002L8.95235 10.3848C9.31445 10.3847 9.67923 10.2497 9.96444 9.96451L9.69251 9.69254ZM8.95227 10.0002V9.61555C8.7839 9.61555 8.61783 9.55432 8.48389 9.42048L8.21204 9.69254L7.94018 9.96459C8.22528 10.2495 8.58999 10.3848 8.95227 10.3848V10.0002ZM8.21204 9.69254L8.48399 9.42058L5.27012 6.20672L4.99817 6.47867L4.72622 6.75062L7.94008 9.96449L8.21204 9.69254ZM4.99817 6.47867L4.72617 6.20676L1.51328 9.42062L1.78528 9.69254L2.05727 9.96445L5.27016 6.75058L4.99817 6.47867ZM1.78528 9.69254L1.50411 9.43012C1.39292 9.54926 1.22188 9.61548 1.04487 9.61555L1.04504 10.0002L1.04522 10.3848C1.39881 10.3846 1.78669 10.2547 2.06644 9.95496L1.78528 9.69254ZM1.04504 10.0002V9.61555C0.876053 9.61555 0.709392 9.55419 0.575787 9.42058L0.303834 9.69254L0.0318803 9.96449C0.3174 10.25 0.683146 10.3848 1.04504 10.3848V10.0002ZM0.303834 9.69254L0.575814 9.42061C0.320867 9.16561 0.320881 8.73795 0.575787 8.48305L0.303834 8.21109L0.0318803 7.93914C-0.523471 8.49449 -0.52336 9.40914 0.0318532 9.96446L0.303834 9.69254ZM0.303834 8.21109L0.575787 8.48305L3.78868 5.27015L3.51672 4.9982L3.24477 4.72625L0.0318803 7.93914L0.303834 8.21109ZM3.51672 4.9982L3.78868 4.72625L0.575787 1.51336L0.303834 1.78531L0.0318803 2.05726L3.24477 5.27015L3.51672 4.9982ZM0.303834 1.78531L0.575787 1.51336C0.320873 1.25844 0.320862 0.830788 0.57581 0.575795L0.303834 0.303865L0.0318571 0.0319349C-0.523376 0.587263 -0.523471 1.50191 0.0318803 2.05726L0.303834 1.78531ZM0.303834 0.303865L0.575787 0.575819C0.830745 0.320861 1.25837 0.320861 1.51333 0.575819L1.78528 0.303865L2.05723 0.0319116C1.50188 -0.523438 0.58723 -0.523438 0.0318803 0.0319116L0.303834 0.303865ZM1.78528 0.303865L1.51333 0.575819L4.72622 3.78871L4.99817 3.51676L5.27012 3.2448L2.05723 0.0319116L1.78528 0.303865ZM4.99817 3.51676L5.27008 3.78875L8.48395 0.57586L8.21204 0.303865L7.94012 0.0318703L4.72626 3.24476L4.99817 3.51676Z" fill="white" mask="url(#path-1-inside-1_1030_1913)" />
                </svg>
              </button>
            </div>
          </div>
        ) : (
          <div className='header-table-filters advanced-table-filters'>
            <div className='left-area'>
              <div className='searchbox'>
                <TextField
                  fullWidth
                  hiddenLabel
                  size="small"
                  name='search'
                  variant="standard"
                  placeholder='Search by name or label'
                  value={searchValue}
                  onChange={(e) => setSearchValue(e.target.value.toLowerCase())}
                />
                <i className='icon'>
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M5.82319 10.6464C3.16455 10.6464 1 8.48183 1 5.82319C1 3.16455 3.16455 1 5.82319 1C8.48183 1 10.6464 3.16455 10.6464 5.82319C10.6464 8.48183 8.48183 10.6464 5.82319 10.6464ZM5.82319 1.70583C3.55041 1.70583 1.70583 3.55511 1.70583 5.82319C1.70583 8.09127 3.55041 9.94055 5.82319 9.94055C8.09597 9.94055 9.94055 8.09127 9.94055 5.82319C9.94055 3.55511 8.09597 1.70583 5.82319 1.70583Z" fill="#505258" stroke="#505258" stroke-width="0.236842" />
                    <path d="M13.3182 13.7068C13.2245 13.7069 13.1307 13.6725 13.0566 13.5985L9.10425 9.63167C8.96097 9.48877 8.96065 9.25192 9.10355 9.10864C9.24646 8.96536 9.4833 8.96505 9.62658 9.10795L13.5789 13.0748C13.7222 13.2177 13.7225 13.4546 13.5796 13.5979C13.5057 13.672 13.412 13.7066 13.3182 13.7068Z" fill="#505258" stroke="#505258" stroke-width="0.236842" />
                  </svg>
                </i>
              </div>
              <div className='filter-box'>
                <Select
                  value={selectedObject}
                  onChange={handleObjectChange}
                  displayEmpty
                  renderValue={(selected) => {
                    if (!selected) {
                      return 'Module';
                    }
                    const selectedItem = objects.find(objectType => objectType['value'] === selected);
                    return selectedItem ? selectedItem['label'] : 'Module';
                  }}
                >
                  {objects.map(objectType => (
                    <MenuItem key={objectType['value']} value={objectType['value']}>
                      {objectType['label']}
                    </MenuItem>
                  ))}
                </Select>

              </div>
              <div className='filter-box'>
                <Select
                  value={selectedGroup}
                  onChange={handleGroupChange}
                  displayEmpty
                  renderValue={(selected) => {
                    if (!selected) return 'Group';
                    if (selected === 'all') return 'All Groups';
                    return selected;
                  }}
                >
                  <MenuItem value="all">All Groups</MenuItem>
                  {Object.keys(properties).map((group) => (
                    <MenuItem key={group} value={group}>
                      {group}
                    </MenuItem>
                  ))}
                </Select>

              </div>
            </div>
          </div>
        )}

        <style>{checkboxStyles}</style>
        <DataTable
          dense
          fixedHeader
          keyField="_id"
          selectableRows
          columns={columns}
          data={allProperties}
          progressPending={isLoading}
          progressComponent={<TableAnimationFiles />}
          // expandableRowsComponent={ExpandedComponent}
          persistTableHead
          paginationPerPage={100}
          pagination={isPaginated}
          fixedHeaderScrollHeight={headerScrollHeight}
          paginationRowsPerPageOptions={[10, 25, 50, 100]}
          className={`table-sellagent ${responsiveClass}`}
          noDataComponent={<div className='notfound-area'>
            <div className='img-box'>
              <img alt="..." src={require('../../../static/img/not-found.png')} />
            </div>
            <p>No properties found</p>
          </div>}
          sortIcon={<CustomSortIcon />}
        />
      </div>
      {renderPropertyForm()}
    </Box>
  )
}

// Options editor component for select/multiselect fields
const OptionsEditor = ({ options, hasColors, onChange }) => {
  const [localOptions, setLocalOptions] = useState(options || []);
  const [colorPickerIndex, setColorPickerIndex] = useState(null);

  const colorPalette = [
    '#EC4899', '#F472B6', '#F87171', '#FB923C', '#FBBF24',
    '#FDE047', '#A3E635', '#FF0000', '#6EE7B7', '#5EEAD4',
    '#22D3EE', '#60A5FA', '#818CF8', '#A78BFA', '#C084FC',
    '#E879F9', '#FB7185', '#FCA5A5', '#FDBA74', '#FCD34D',
    '#BEF264', '#86EFAC', '#67E8F9', '#93C5FD', '#C4B5FD'
  ];

  // Update local options when prop changes
  useEffect(() => { setLocalOptions(options || []) }, [options]);

  // Don't call onChange immediately when adding empty option
  const addOption = () => setLocalOptions([...localOptions, { value: '', label: '', ...(hasColors ? { color: '#6B7280' } : {}) }]);

  const updateOption = (index, field, value) => {
    const updated = [...localOptions];
    const oldOption = localOptions[index];

    if (field === 'label') {
      // Check if the current value was auto-generated from the previous label
      const oldLabel = oldOption.label || '';
      const currentValue = oldOption.value || '';
      const expectedAutoValue = oldLabel.toLowerCase().replace(/[^a-z0-9_]/g, '_');
      const isAutoGenerated = currentValue === '' || currentValue === expectedAutoValue;

      // Update label
      updated[index] = { ...updated[index], label: value };

      // Auto-generate value from new label if it was previously auto-generated
      if (isAutoGenerated) {
        // Convert label to a valid internal value (lowercase, replace spaces/special chars with underscore)
        updated[index].value = value.toLowerCase().replace(/[^a-z0-9_]/g, '_');
      }
    } else {
      // Update value directly
      updated[index] = { ...updated[index], [field]: value };
    }

    setLocalOptions(updated);
    // Call onChange with all options, let parent handle filtering
    onChange(updated);
  }

  const removeOption = (index) => {
    const updated = localOptions.filter((_, i) => i !== index);
    setLocalOptions(updated);
    onChange(updated);
  };

  return (
    <Box>
      {localOptions.length === 0 ? (
        <Box sx={{ p: 2, textAlign: 'center', color: 'text.secondary' }}>
          No options defined. Click "Add Option" to get started.
        </Box>
      ) : (
        localOptions.map((option, index) => (
          <Box key={index} display="flex" gap={1} mb={1} alignItems="flex-start">
            {hasColors && (
              <Box sx={{ position: 'relative', mt: 0.5 }}>
                <Box
                  onClick={() => setColorPickerIndex(colorPickerIndex === index ? null : index)}
                  sx={{
                    width: 32, height: 32, borderRadius: '50%', cursor: 'pointer',
                    backgroundColor: option['color'] || '#6B7280',
                    border: '2px solid #e5e7eb', flexShrink: 0,
                    '&:hover': { border: '2px solid #9ca3af' }
                  }}
                />
                {colorPickerIndex === index && (
                  <Box sx={{
                    position: 'absolute', top: 38, left: 0, zIndex: 10,
                    backgroundColor: '#fff', borderRadius: 2, boxShadow: 3,
                    p: 1.5, display: 'flex', flexWrap: 'wrap', gap: 0.5, width: 180
                  }}>
                    {colorPalette.map((color) => (
                      <Box
                        key={color}
                        onClick={() => { updateOption(index, 'color', color); setColorPickerIndex(null); }}
                        sx={{
                          width: 24, height: 24, borderRadius: '50%', cursor: 'pointer',
                          backgroundColor: color,
                          border: option['color'] === color ? '2px solid #1A0737' : '2px solid transparent',
                          '&:hover': { transform: 'scale(1.2)' }, transition: 'transform 0.15s'
                        }}
                      />
                    ))}
                  </Box>
                )}
              </Box>
            )}
            <TextField
              size="small"
              sx={{ flex: 1 }}
              placeholder="Label (display)"
              value={option['label'] || ''}
              onChange={(e) => updateOption(index, 'label', e['target']['value'])}
            />
            <TextField
              size="small"
              sx={{ flex: 1 }}
              value={option['value'] || ''}
              placeholder="Value (auto-generated)"
              onChange={(e) => updateOption(index, 'value', e['target']['value'])}
            />
            <IconButton
              size="small"
              onClick={() => removeOption(index)}
              sx={{ mt: 0.5 }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Box>
        ))
      )}
      <Button size="small" onClick={addOption} startIcon={<AddIcon />}>
        Add Option
      </Button>
    </Box>
  )
}

const ResizableHeader = ({ title, width, onResize }) => {
  const startX = useRef(0);
  const startWidth = useRef(0);

  const handleMouseDown = (e) => {
    e.preventDefault();
    startX.current = e['clientX'];
    startWidth.current = width;

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  };

  const onMouseMove = (e) => {
    const delta = e['clientX'] - startX.current;
    const newWidth = startWidth.current + delta;
    if (newWidth >= 60) onResize(newWidth);
  };

  const onMouseUp = () => {
    document.removeEventListener('mousemove', onMouseMove);
    document.removeEventListener('mouseup', onMouseUp);
  };

  return (
    <div style={{ position: 'relative', height: '100%', padding: '0 10px' }}>
      <div style={{ padding: '6px 0' }}>{title}</div>
      <div
        onMouseDown={handleMouseDown}
        style={{
          position: 'absolute',
          right: 0,
          top: 0,
          height: '100%',
          width: '5px',
          cursor: 'col-resize',
          backgroundColor: 'transparent',
        }}
      />
    </div>
  );
};

const checkboxStyles = `
  .table-sellagent input[type="checkbox"] {
    width: 13px;
    height: 13px;
    cursor: pointer;
    accent-color: #cbd6e2;
    transform: scale(1);
    opacity: 0.45;
  }

  .table-sellagent input[type="checkbox"]:checked {
    opacity: 1;
    accent-color: var(--primary-color);
  }
`;

const CustomSwitch = styled(Switch)(() => ({
  width: 35,
  height: 16,
  padding: 0,
  display: 'flex',
  '& .MuiSwitch-switchBase': {
    padding: 2,
    '&.Mui-checked': {
      transform: 'translateX(15px)',
      color: '#fff',
      '& + .MuiSwitch-track': {
        backgroundColor: '#1A0737', // dark purple when active
        opacity: 1,
        border: 0,
      },
    },
  },
  '& .MuiSwitch-thumb': {
    boxShadow: '0 2px 4px 0 rgb(0 35 11 / 20%)',
    width: 12,
    height: 12,
    borderRadius: '50%',
    backgroundColor: '#fff',
  },
  '& .MuiSwitch-track': {
    borderRadius: 26 / 2,
    opacity: 1,
    backgroundColor: '#BDBDBD', // light gray when inactive
    boxSizing: 'border-box',
    position: 'absolute',
    left: '0',
    width: '30px',
  },
}));

export default PropertyManagement;